import { isEqual } from 'lodash';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import scrollToElement from 'scroll-to-element';

import { BANNER_TYPES } from '@sravni/react-advertisement';
import { NotificationManager, Typography } from '@sravni/react-design-system';
import { useBoolean, useIsMobile, usePrevious } from '@sravni/react-utils';
import { PAGE_TYPE } from '@sravni/ugc/lib/organizations/constants';

import { META_DISCLAIMER } from '../../constants/common';
import { DEFAULT_FILTERS, RATES_OPTIONS } from '../../constants/reviews';
import { LIST } from '../../constants/routes';
import { getAnalyticsObject } from '../../helpers/analyticsEvents';
import { checkTriggerWords } from '../../helpers/common';
import { REVIEW_TYPE_LIST_ROUTE_MAP, ReviewType } from '../../helpers/reviews';
import { useIsWlPage } from '../../hooks/useIsWlPage';
import { useParamsDfp } from '../../hooks/useParamsDfp';
import { useTabsForSpecialOffers } from '../../hooks/useTabsForSpecialOffers';
import { ICommentsListQuery, IReviewItem } from '../../interfaces/reviews';
import { getVotes, increaseViews, loadViews } from '../../services/reviews';
import { useBanners, useLoadingBanners } from '../../store/hooks/advertising';
import { useComments, useCommentsList } from '../../store/hooks/comments';
import { useProductsTags, useServerFilters } from '../../store/hooks/filters';
import { useOrganizationDetails } from '../../store/hooks/organizations';
import { useProductDetails } from '../../store/hooks/products';
import { useReviewItem, useReviewsList } from '../../store/hooks/reviews';
import { useFingerprint } from '../../store/hooks/users';
import { fetchBanners } from '../../store/reducers/advertising';
import { fetchCommentsList } from '../../store/reducers/comments';
import { doDeleteReview, fetchReviewsList } from '../../store/reducers/reviews';
import { TReviewListQuery } from '../../types/router';
import { createLinkProps } from '../../utils/routing';
import { AuthPopup } from '../AuthPopup';
import { BackLink } from '../BackLink';
import { BorderedBanner } from '../BorderedBanner';
import { CommentsList } from '../CommentsList';
import { ProductBlock } from '../ProductBlock';
import { ReviewCard } from '../ReviewCard';
import { ReviewsListContent } from '../ReviewsList/components/Content';
import { ReviewSkeleton } from '../Skeletons/review';
import { SpecialOffersIdlePopup } from '../SpecialOffersIdlePopup';

interface IProps {
  reviewTypes: ReviewType;
  sideBlockDescription: string;
}

export const ReviewPage: React.FC<IProps> = ({ reviewTypes, sideBlockDescription }) => {
  const isMobile = useIsMobile();
  const router = useRouter() as TRouter<TReviewListQuery>;
  const { query } = router;
  const dispatch = useDispatch();
  const banners = useBanners();
  const isLoadingBanners = useLoadingBanners();
  const fp = useFingerprint();
  const { item: reviewFromState } = useReviewItem();
  const [review, setReview] = useState<IReviewItem>(reviewFromState);
  const comments = useComments();
  const commentsList = useCommentsList();
  const organization = useOrganizationDetails(query.organization);
  const productsTags = useProductsTags();
  const serverFilters = useServerFilters();
  const reviews = useReviewsList();
  const isWl = useIsWlPage();

  const commentsRef = useRef<HTMLDivElement>();
  const companyAnswerRef = useRef<HTMLDivElement>();

  const [isDeleting, setIsDeleting] = useBoolean(false);
  const [authPopupVisible, setAuthPopupVisible] = useBoolean(false);
  const [views, setViews] = useState<number>(null);

  const showAuthPopup = () => setAuthPopupVisible.on();
  const closeAuthPopup = () => setAuthPopupVisible.off();

  const { paramsDfp, paramsForDecoratingBanners } = useParamsDfp({
    tag: review.reviewTag,
    type: query.type,
  });
  const specialOffersTabs = useTabsForSpecialOffers({ reviewTag: review.reviewTag, type: query.type });
  const previousParamsDfp = usePrevious(paramsDfp);

  const currentServerFilters = serverFilters[reviewTypes.typeForService];
  const typePage = reviewTypes.itemTypeUrl;
  const isPartner = organization?.partnerCard || organization?.isRecomended;
  const companyAnswer = commentsList.commentCompanyId && comments[commentsList.commentCompanyId];
  const gaObj = getAnalyticsObject(organization.name, router.query.type as PAGE_TYPE, productsTags, review.id);

  const product = useProductDetails(review.specificProductId);

  const [page, setPage] = useState<number>(0);
  const prevPage = usePrevious(page);

  const filterForFetching = useMemo(
    () => ({
      ...DEFAULT_FILTERS,
      tag: review.reviewTag,
      reviewObjectId: review.reviewObjectId,
      rated: isPartner ? RATES_OPTIONS.GOOD : RATES_OPTIONS.ANY,
    }),
    [isPartner, review.reviewObjectId, review.reviewTag],
  );

  const prevFilterForFetching = usePrevious(filterForFetching);

  const listUrl = createLinkProps('/list', LIST, {
    type: REVIEW_TYPE_LIST_ROUTE_MAP[review.reviewObjectType],
  });

  const fetchComments = useCallback(
    async (params: ICommentsListQuery) => {
      await dispatch(fetchCommentsList(params));
    },
    [dispatch],
  );

  const increaseReviewViews = useCallback(async (reviewId: number) => {
    await increaseViews(reviewId);
  }, []);

  const fetchReviewViews = useCallback(async (reviewId: number) => {
    try {
      const res = await loadViews({ ids: [reviewId] });
      setViews(res[0]?.views);
    } catch (e) {}
  }, []);

  useEffect(() => {
    increaseReviewViews(review.id);
    fetchReviewViews(review.id);
  }, [review.id, fetchReviewViews, increaseReviewViews]);

  useEffect(() => {
    if (isEqual(previousParamsDfp, paramsDfp)) return;

    dispatch(fetchBanners({ params: paramsDfp, paramsForDecorating: paramsForDecoratingBanners }));
  }, [dispatch, paramsDfp, paramsForDecoratingBanners, previousParamsDfp]);

  const wasMetaDisclaimerVisible = useRef<boolean>(false);

  useEffect(() => {
    if (wasMetaDisclaimerVisible.current) return;

    const titleHas = checkTriggerWords(review.title);
    const textHas = checkTriggerWords(review.text);
    const nameHas = checkTriggerWords(review.authorName);
    const commentsHas =
      commentsList?.list?.length &&
      commentsList.list.some(
        (id) => checkTriggerWords(comments[id].text) || checkTriggerWords(comments[id].authorName),
      );

    if (titleHas || textHas || nameHas || commentsHas) {
      NotificationManager.show({
        message: META_DISCLAIMER,
        timeout: 1000 * 60 * 60,
      });
      wasMetaDisclaimerVisible.current = true;
    }
  }, [comments, commentsList.list, review]);

  useEffect(() => {
    let timer = null;

    if (window.location.hash === '#comments') {
      timer = setTimeout(() => scrollToElement(commentsRef.current, { offset: -10 }), 300);
    }

    if (window.location.hash === '#companyAnswer') {
      timer = setTimeout(() => scrollToElement(companyAnswerRef.current, { offset: -10 }), 300);
    }

    return () => {
      timer && clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const reviewId = reviewFromState.id;
    const fetchLikesAndSetReview = async () => {
      const { data: votes } = await getVotes({
        ReviewIds: [reviewId],
        Fingerprint: fp,
      });

      const voteForReview = votes?.[reviewId];

      setReview((review) => {
        const reviewWithLikes = {
          ...review,
          vote: voteForReview?.userVote ?? null,
          votes: voteForReview?.votes ?? { positive: 0, negative: 0 },
        };

        return reviewWithLikes;
      });
    };

    fetchLikesAndSetReview();
  }, [fp, reviewFromState.id]);

  const reviewsWithoutMainReview = reviews.items?.filter((item) => item.id !== review.id);

  const reviewsList = {
    ...reviews,
    items: reviewsWithoutMainReview,
    total: reviews.total - 1,
  };

  const hasMoreReviews = reviewsList.total > reviewsList.items.length;
  const canLoadingMoreReviews = reviewsList.total > 1;
  const isReviewsListLoading = reviewsList.loading || reviewsList.isLoadingMore;

  useEffect(() => {
    if (isEqual(prevFilterForFetching, filterForFetching) && page === prevPage) return;

    dispatch(
      fetchReviewsList({
        filter: {
          ...filterForFetching,
          page,
        },
        organization,
      }),
    );
  }, [dispatch, filterForFetching, organization, page, prevFilterForFetching, prevPage]);

  const handlePageScroll = () => {
    if (!hasMoreReviews || !canLoadingMoreReviews || isReviewsListLoading) return;

    setPage((page) => page + 1);
  };

  if (isDeleting && !review) {
    return <ReviewSkeleton />;
  }

  const handleDelete = async () => {
    setIsDeleting.on();
    try {
      await dispatch(doDeleteReview({ id: review.id }));
    } catch (e) {
      throw e;
    } finally {
      window.location.href = listUrl.as;
      setIsDeleting.off();
    }
  };

  return (
    <div>
      {isMobile && <BackLink type={reviewTypes.listTypeUrl} />}
      <ReviewCard
        review={review}
        companyAnswer={companyAnswer}
        reviewTypes={reviewTypes}
        handleDelete={handleDelete}
        tags={currentServerFilters?.tag}
        type={reviewTypes.itemTypeUrl}
        showAuthPopup={showAuthPopup}
        commentsRef={commentsRef}
        companyAnswerRef={companyAnswerRef}
        sideBlockDescription={sideBlockDescription}
        views={views}
        isMobile={isMobile}
      />
      {review.specificProductId && review.rating > 2 && product && (
        <ProductBlock organization={organization} productTag={review.reviewTag} product={product} />
      )}
      {!isWl && isMobile && (isLoadingBanners || banners?.tgbm7) && (
        <BorderedBanner banner={banners?.tgbm7} lazy={true} className={'h-mb-16'} type={BANNER_TYPES.TGBM_7} />
      )}
      {!review.isExternal && (
        <div ref={commentsRef}>
          <CommentsList
            reviewId={review?.id}
            fetchComments={fetchComments}
            analyticLabel={gaObj}
            reviewTypes={reviewTypes}
            logo={review.logo?.square || review.logo?.round}
            organizationId={organization.id}
            showAuthPopup={showAuthPopup}
          />
        </div>
      )}
      {!isWl && isMobile && (isLoadingBanners || banners?.side) && (
        <BorderedBanner banner={banners?.side} lazy={true} type={BANNER_TYPES.SIDE} />
      )}
      {!isWl && !isMobile && (isLoadingBanners || banners?.tgbm7) && (
        <BorderedBanner banner={banners?.tgbm7} lazy={true} className={'h-mb-32'} type={BANNER_TYPES.TGBM_7} />
      )}
      {reviews.total > 0 && (
        <div>
          <Typography.Heading level={3} className={isMobile ? 'h-mt-32 h-mb-16 h-ml-20' : 'h-mb-32'}>
            Другие отзывы{' '}
          </Typography.Heading>
          <ReviewsListContent
            type={typePage}
            tags={currentServerFilters?.tag}
            reviews={reviewsList}
            reviewTypes={reviewTypes}
            specialOffersTabs={specialOffersTabs}
            handlePageScroll={handlePageScroll}
            isReviewPage
          />
        </div>
      )}
      <SpecialOffersIdlePopup tabs={specialOffersTabs} />
      <AuthPopup showPopup={authPopupVisible} handleClose={closeAuthPopup} />
    </div>
  );
};
